import EpDevPUtils from "./EpDevPUtils";
import { 
  IEpDevPEntityIdObject,
  TEpDevPEntityId, 
  TEpDevPEntityIdList 
} from "./types";

class EpDevPEntityIdsService {
  // private readonly ComponentName = "EpDevPEntityIdsService";

  public getSortedDisplayNameList = (list: TEpDevPEntityIdList): Array<string> => {
    if(list.length > 0) {
      const sortedList = this.sortByDisplayName(list);
      return sortedList.map((x) => { return x.displayName; });
    }
    else return [];
  }

  public getSortedDisplayNameListAsString = (list: TEpDevPEntityIdList): string => {
    if(list.length > 0) return this.getSortedDisplayNameList(list).join(', ');
    else return '';
  }

  public sortByDisplayName(list: TEpDevPEntityIdList): TEpDevPEntityIdList {
    return list.sort( (e1: TEpDevPEntityId, e2: TEpDevPEntityId) => {
      if(e1.displayName.toLocaleLowerCase() < e2.displayName.toLocaleLowerCase()) return -1;
      if(e1.displayName.toLocaleLowerCase() > e2.displayName.toLocaleLowerCase()) return 1;
      return 0;
    });
  }

  public createEmptyObject = (generateId: boolean): TEpDevPEntityId => {
    return {
      id: generateId ? EpDevPUtils.getUUID() : '',
      displayName: ''
    };
  }

  /**
   * De-duplicates the entityIdList.
   * Returns the de-duplicated list with a deep copy of the objects.
   */
  public getDedupedListFromEntityIdObjectList<T extends IEpDevPEntityIdObject>(list: Array<T>): Array<T> {
    const unique = new Map<string, number>();
    const distinct: Array<T> = [];
    for(let i=0; i < list.length; i++) {
      if(!unique.has(list[i].entityId.id)) {
        distinct.push(list[i]);
        unique.set(list[i].entityId.id, 1);
      }
    }
    return JSON.parse(JSON.stringify(distinct));
  }
  
  public createDedupedList = (list: TEpDevPEntityIdList): TEpDevPEntityIdList => {
    const unique = new Map<string, number>();
    const distinct: TEpDevPEntityIdList = [];
    for(let i=0; i < list.length; i++) {
      if(!unique.has(list[i].id)) {
        distinct.push(list[i]);
        unique.set(list[i].id, 1);
      }
    }
    return distinct;
  }

  public createListFromIdList(idList: Array<string>): TEpDevPEntityIdList {
    return idList.map( (x: string) => {
      return { id: x, displayName: x };
    });
  }

  public createDisplayNameList(list: TEpDevPEntityIdList): Array<string> {
    return list.map( (x) => {
      return x.displayName;
    });
  }

  public createSortedDisplayNameList(list: TEpDevPEntityIdList): Array<string> {
    return this.createDisplayNameList(this.sortByDisplayName(list));
  }

  public createIdList(list: TEpDevPEntityIdList): Array<string> {
    return list.map( (x) => {
      return x.id;
    });
  }

  public filterByIdList({epDevPEntityIdList, idList}: {
    epDevPEntityIdList: TEpDevPEntityIdList; 
    idList: Array<string>;
  }): TEpDevPEntityIdList {
    return epDevPEntityIdList.filter( (x) => {
      return idList.includes(x.id);
    });
  }

  public filterEntityIdObjectListByEntityIdList<T extends IEpDevPEntityIdObject>({ epDevPEntityIdObjectList, epDevPEntityIdList }:{
    epDevPEntityIdObjectList: Array<T>;
    epDevPEntityIdList: TEpDevPEntityIdList;
  }): Array<T> {
    if(epDevPEntityIdObjectList.length === 0) return [];
    if(epDevPEntityIdList.length === 0) return [];
    return epDevPEntityIdObjectList.filter( (x: T) => {
      const idx = epDevPEntityIdList.findIndex( (y) => {
        return x.entityId.id === y.id;
      });
      return (idx > -1);
    });
  }

  public filterEntityIdObjectListByNotEntityIdList<T extends IEpDevPEntityIdObject>({ epDevPEntityIdObjectList, not_epDevPEntityIdList }:{
    epDevPEntityIdObjectList: Array<T>;
    not_epDevPEntityIdList: TEpDevPEntityIdList;
  }): Array<T> {
    if(epDevPEntityIdObjectList.length === 0) return [];
    if(not_epDevPEntityIdList.length === 0) return epDevPEntityIdObjectList;
    return epDevPEntityIdObjectList.filter( (x: T) => {
      const idx = not_epDevPEntityIdList.findIndex( (y) => {
        return x.entityId.id === y.id;
      });
      return (idx === -1);
    });
  }

  public sortEntityIdObjectListByDisplayName<T extends IEpDevPEntityIdObject>(list: Array<T>): Array<T> {
    return list.sort( (e1: T, e2: T) => {
      if(e1.entityId.displayName.toLocaleLowerCase() < e2.entityId.displayName.toLocaleLowerCase()) return -1;
      if(e1.entityId.displayName.toLocaleLowerCase() > e2.entityId.displayName.toLocaleLowerCase()) return 1;
      return 0;
    });
  }

  public getSortedDisplayNameListFromEntityIdObjectList<T extends IEpDevPEntityIdObject>(list: Array<T>): Array<string> {
    return list.map( (entityIdObject: T) => {
      return entityIdObject.entityId.displayName;
    }).sort( (e1: string, e2:string) => {
      if(e1.toLocaleLowerCase() < e2.toLocaleLowerCase()) return -1;
      if(e1.toLocaleLowerCase() > e2.toLocaleLowerCase()) return 1;
      return 0;
    });
  }

  public getIdListFromEntityIdObjectList<T extends IEpDevPEntityIdObject>(list: Array<T>): Array<string> {
    return list.map( (entityIdObject: T) => {
      return entityIdObject.entityId.id;
    });
  }

  public getEntityIdListFromEntityIdObjectList<T extends IEpDevPEntityIdObject>(list: Array<T>): TEpDevPEntityIdList {
    return list.map( (entityIdObject: T) => {
      return entityIdObject.entityId;
    });
  }

}

export default new EpDevPEntityIdsService();
